import './DefaultCards.css';
import React from 'react';
import { get, getGlobalDictionaryTerm } from '../../../utils';
import { Text, RichText, Link } from '@sitecore-jss/sitecore-jss-react';
import RouterLink from '../../atoms/RouterLink/RouterLink';
import LazyPicture from '../../atoms/LazyPicture';
import { tealiumMergeClickNewData, tealiumClickFunc } from '../../../utils/tealium';
import ScrollAnimation from 'react-animate-on-scroll';
import { convertToAnalyticObject } from '../../../utils/analytics';
import CarouselProvider from '../../atoms/CarouselProvider/CarouselProvider';
import { useSitecoreContext } from '../../../utils/SitecoreContextReactContext';
import { ReactComponent as IconChevronRightLight } from '../../../resources/icons/IconChevronRightLight.svg';
import { ReactComponent as IconChevronLeftLight } from '../../../resources/icons/IconChevronLeftLight.svg';
const Card = _ref => {
  let {
    fields,
    params,
    inCarousel = false
  } = _ref;
  const sitecoreContext = useSitecoreContext();
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const pageEditing = Boolean(sitecoreContext && sitecoreContext.pageEditing);
  const newWindowLabel = getGlobalDictionaryTerm(sitecoreContext, 'newWindowLabel') || "Opens in a new window";
  const fieldsData = get(fields, 'data');
  const item = get(fieldsData, 'item');
  const imageSrc = get(item, 'image.jss.value.src');
  const imageAlt = get(item, 'image.jss.value.alt');
  const hasTitle = get(item, 'title.jss');
  const hasTag = get(item, 'tag.jss.value');
  const tag = get(item, 'tag.jss');
  const hasDescription = get(item, 'description.jss');
  const ctaLabel = get(item, 'ctaLabel.jss.value');
  const ctaLink = get(item, 'ctaLink.jss.value.href');
  const ctaLinktype = get(item, "ctaLink.jss.value.linktype");
  const cardTitle = get(item, 'title.jss.value');
  let eventsAnalytics = get(fields, 'data.eventsAnalytics') || [];
  if (eventsAnalytics.length) {
    eventsAnalytics = convertToAnalyticObject(eventsAnalytics);
  }
  const renderContentInLink = children => {
    if (!ctaLink && !pageEditing) return children;
    let linkTitle = '';
    if (hasTitle && hasTitle.value) {
      linkTitle = hasTitle.value;
    } else {
      linkTitle = imageAlt ? imageAlt : '';
    }
    if (ctaLabel) {
      linkTitle = `${ctaLabel} | ${linkTitle}`;
    }
    if (item.ctaLink.jss && item.ctaLink.jss.value && item.ctaLink.jss.value.target === "_blank") {
      linkTitle = linkTitle ? `${linkTitle} | ${newWindowLabel}` : newWindowLabel;
    }
    return /*#__PURE__*/React.createElement(Link, {
      "aria-label": linkTitle,
      field: item.ctaLink.jss
    }, children);
  };
  const renderLink = () => {
    if (!ctaLabel || !ctaLink) {
      return null;
    }
    if (pageEditing && ctaLink) {
      return /*#__PURE__*/React.createElement(Link, {
        field: item.ctaLink.jss
      });
    }
    return /*#__PURE__*/React.createElement("div", {
      className: "c--link-more"
    }, /*#__PURE__*/React.createElement(RouterLink, {
      className: "general-card-button",
      href: ctaLink,
      title: ctaLabel,
      linktype: ctaLinktype,
      onClick: () => tealiumClickFunc(tealiumMergeClickNewData(eventsAnalytics['linkClickAnalytics'], params), [cardTitle, ctaLabel])
    }, /*#__PURE__*/React.createElement("span", {
      className: "btn-tertiary__text"
    }, ctaLabel), ctaLabel && ctaLink ? rtl ? /*#__PURE__*/React.createElement(IconChevronLeftLight, null) : /*#__PURE__*/React.createElement(IconChevronRightLight, null) : null));
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "card--item__img"
  }, /*#__PURE__*/React.createElement("div", {
    className: "card-box theme-contrast"
  }, imageSrc && renderContentInLink(/*#__PURE__*/React.createElement(LazyPicture, {
    useEmptyPlaceholder: true,
    hover: true,
    onClick: () => {
      tealiumClickFunc(tealiumMergeClickNewData(eventsAnalytics['bannerClickAnalytics'], params), cardTitle);
    }
  }, /*#__PURE__*/React.createElement("source", {
    media: "(min-width: 768px)",
    srcSet: `${imageSrc}&cw=500&ch=375`
  }), /*#__PURE__*/React.createElement("img", {
    src: `${imageSrc}&cw=500&ch=375`,
    alt: imageAlt
  }))), /*#__PURE__*/React.createElement("div", {
    className: "c-body"
  }, hasTag && /*#__PURE__*/React.createElement(Text, {
    tag: "p",
    className: "b__title",
    field: tag
  }), hasTitle && hasTitle.value && hasTitle.value.trim() && renderContentInLink(/*#__PURE__*/React.createElement(Text, {
    tag: "h2",
    className: "h4 b__title-2",
    field: hasTitle,
    "data-cy": "general-card-image-title",
    onClick: () => {
      tealiumClickFunc(tealiumMergeClickNewData(eventsAnalytics['titleClickAnalytics'], params), cardTitle);
    }
  })), hasDescription && /*#__PURE__*/React.createElement(RichText, {
    tag: "div",
    className: "body-large b__desc",
    field: hasDescription,
    "data-cy": "general-card-image-description"
  }), renderLink())));
};
const DefaultCards = _ref2 => {
  let {
    fields,
    params
  } = _ref2;
  const sitecoreContext = useSitecoreContext();
  const rtl = Boolean(sitecoreContext && sitecoreContext.rtl);
  const hasTitle = Boolean(get(fields, 'data.item.title.jss.value'));
  const title = get(fields, 'data.item.title.jss.value');
  const hasDescription = Boolean(get(fields, 'data.item.description.jss.value'));
  const childrenItems = get(fields, 'data.item.children', []);
  const items = childrenItems.length ? childrenItems.filter(item => item.title && item.description) : [];
  const ctaLabel = get(fields, 'data.item.ctaLabel.jss.value');
  const ctaLink = get(fields, 'data.item.ctaLink.jss.value.href');
  const ctaLinktype = get(fields, 'data.item.ctaLink.jss.value.linktype');
  const ctaLeftIcon = get(fields, 'data.item.ctaLeftIcon.targetItem.svg.jss.value');
  const ctaRightIcon = get(fields, 'data.item.ctaRightIcon.targetItem.svg.jss.value');
  let eventsAnalytics = get(fields, 'data.eventsAnalytics') || [];
  if (eventsAnalytics.length) {
    eventsAnalytics = convertToAnalyticObject(eventsAnalytics);
  }
  const renderButtonLink = () => {
    return /*#__PURE__*/React.createElement("div", {
      className: "btnLink"
    }, /*#__PURE__*/React.createElement(RouterLink, {
      className: `btn btn-secondary`,
      href: ctaLink,
      title: ctaLabel,
      isDetSite: false,
      linktype: ctaLinktype,
      ctaLeftIcon: ctaLeftIcon,
      ctaRightIcon: ctaRightIcon,
      colorText: `var(--main-color-text)`,
      color: `var(--main-color)`,
      colorActive: `var(--main-color--active)`,
      onClick: () => tealiumClickFunc(tealiumMergeClickNewData(eventsAnalytics['seeMoreAnalytics'], params), [title, ctaLabel])
    }, ctaLabel));
  };
  const renderItems = () => {
    return items.map((item, index) => /*#__PURE__*/React.createElement(ScrollAnimation, {
      className: "default-cards__item",
      animateIn: "animate__fadeInUp",
      animateOnce: true,
      duration: 1 + index / 3,
      key: item.id
    }, /*#__PURE__*/React.createElement(Card, {
      fields: {
        data: {
          eventsAnalytics,
          item
        }
      },
      params: params,
      inCarousel: true
    })));
  };
  return /*#__PURE__*/React.createElement(StyledWrapper, {
    rtl: rtl
  }, items.length < 5 && (hasTitle || hasDescription) ? /*#__PURE__*/React.createElement("div", {
    className: "h__txt"
  }, hasTitle ? /*#__PURE__*/React.createElement(Text, {
    tag: "h3",
    className: "h3 h__title componentTitle",
    field: fields.data.item.title.jss
  }) : null, hasDescription ? /*#__PURE__*/React.createElement(Text, {
    tag: "p",
    className: "p componentDescription",
    field: fields.data.item.description.jss
  }) : null) : null, items.length < 5 ? /*#__PURE__*/React.createElement("div", {
    className: "itemsContainer"
  }, renderItems()) : null, items.length >= 5 ? /*#__PURE__*/React.createElement(CarouselProvider, null, _ref3 => {
    let {
      renderCarousel,
      renderPreviousButton,
      renderNextButton,
      renderDots
    } = _ref3;
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
      className: `${hasTitle || hasDescription ? 'card__head' : 'cardHeadOnlyWithPagination'}`
    }, hasTitle || hasDescription ? /*#__PURE__*/React.createElement("div", {
      className: "h__txt"
    }, hasTitle ? /*#__PURE__*/React.createElement(Text, {
      tag: "h3",
      className: "h3 h__title componentTitle",
      field: fields.data.item.title.jss
    }) : null, hasDescription ? /*#__PURE__*/React.createElement(Text, {
      tag: "p",
      className: "p componentDescription",
      field: fields.data.item.description.jss
    }) : null) : null, /*#__PURE__*/React.createElement("div", {
      className: "pagination__btn"
    }, /*#__PURE__*/React.createElement("div", {
      className: `btn-group--right ${rtl ? 'btn-group-rtl' : ''}`
    }, renderPreviousButton(() => tealiumClickFunc(tealiumMergeClickNewData(eventsAnalytics['prevNavAnalytics'], params))), renderNextButton(() => tealiumClickFunc(tealiumMergeClickNewData(eventsAnalytics['nextNavAnalytics'], params)))))), /*#__PURE__*/React.createElement("div", {
      className: "card-container__inner"
    }, renderCarousel(renderItems(), {
      responsive: {
        extra: {
          breakpoint: {
            max: 10000,
            min: 1101
          },
          items: 4
        },
        desktop: {
          breakpoint: {
            max: 1100,
            min: 900
          },
          items: 3
        },
        table_portrait: {
          breakpoint: {
            max: 899,
            min: 768
          },
          items: 2
        },
        tablet: {
          breakpoint: {
            max: 767,
            min: 480
          },
          items: 2
        },
        mobile: {
          breakpoint: {
            max: 479,
            min: 0
          },
          items: 1
        }
      },
      containerClass: "carouselContainer"
    }, () => tealiumClickFunc(tealiumMergeClickNewData(eventsAnalytics['mobileSlidesAnalytics'], params)))), ctaLink && ctaLabel && items.length >= 5 ? /*#__PURE__*/React.createElement("div", {
      className: "paginationAndLinkContainer"
    }, renderButtonLink(), /*#__PURE__*/React.createElement("div", {
      className: "pagination-dot"
    }, renderDots(() => tealiumClickFunc(tealiumMergeClickNewData(eventsAnalytics['navBubblesAnalytics'], params))))) : /*#__PURE__*/React.createElement("div", {
      className: "paginationWithoutLinkContainer"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pagination-dot"
    }, renderDots(() => tealiumClickFunc(tealiumMergeClickNewData(eventsAnalytics['navBubblesAnalytics'], params))))));
  }) : null);
};
const StyledWrapper = _ref4 => {
  let {
    children,
    rtl,
    className,
    style
  } = _ref4;
  return /*#__PURE__*/React.createElement("div", {
    style: style,
    className: `${commonCardStyles} ${rtl ? rtlStyles : ltrStyles} ${className}`
  }, children);
};
export const commonCardStyles = "c1wxwe88";
const rtlStyles = "r828gkj";
const ltrStyles = "l1lvlvq9";
export { DefaultCards };